'use client';

// Spec 113 — the shared "Market details" step (step 2) for BOTH creation
// methods. Single-form editable view holding cover image + title + ticker +
// description + category + location + outcomes on ONE screen.
//   - Manual path: `aiStreamStatus === 'idle'`, so `pending()` is always
//     false and every field renders immediately (seeded title, blank rest).
//   - AI path: streamed data fills the same fields as each SSE event arrives;
//     a field shows a `<Skeleton>` only while streaming AND still empty.
//
// Visual structure mirrors the legacy manual per-step layouts so the user
// recognises the controls — only the page count differs:
//   - Cover image (left)  |  Title + Description (right)
//   - Market ticker (full row, $ prefix)
//   - Category | Location (2-col grid)
//   - Outcomes — one `<OutcomeInput>` per row.
//
// Image affordances follow the manual-flow pattern: tap the populated
// thumbnail to re-open `<MarketImagePickerModal>` and replace the image.
// A small X overlay clears the slot. There are no "Retry image" buttons —
// retrying is just opening the picker again.
//
// Each field renders a `<Skeleton>` placeholder while the AI is streaming
// AND the corresponding store field is still empty; the moment a streamed
// value arrives, the editable input replaces the skeleton. After streaming
// completes, every field is editable regardless of whether it's empty.
import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import type { MarketImageDraft } from '@/components/images/market-picker';
import { MarketImagePickerModal } from '@/components/images/market-picker-modal';
import { AiStreamStatusChip } from '@/components/launchpad/ai/ai-stream-status-chip';
import { SkeletonField } from '@/components/launchpad/ai/skeleton-field';
import { StreamedImageSlot } from '@/components/launchpad/ai/streamed-image-slot';
import { OutcomeInput } from '@/components/launchpad/outcome-input';
import { SimilarMarketsNudge } from '@/components/launchpad/similar-markets-nudge';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { Alert01IconSolid, PlusSignIcon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select-field';
import { TextareaField } from '@/components/ui/textarea-field';
import { PROPOSAL_FIELDS } from '@/config/proposal-fields';
import { PROPOSAL_CATEGORIES, PROPOSAL_LOCATIONS } from '@/config/proposal-options';
import { useOutcomesLimits } from '@/hooks/launchpad/use-outcomes-limits';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { detectPredictionStyle } from '@/lib/launchpad/detect-prediction-style';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { getDetailsStepErrors, type StepErrors, selectStepAttempted, useProposalFormStore } from '@/stores/proposal-form';

/** Spec 113 — the consolidated details step is step 2 of the unified flow,
 *  so its inline errors gate on `stepsAttempted.has(2)` (FR-007). */
const DETAILS_STEP = 2;
const FADE_INITIAL = {
  opacity: 0,
  y: 4
} as const;
const FADE_ANIMATE = {
  opacity: 1,
  y: 0
} as const;
type AiOutcome = ReturnType<typeof useProposalFormStore.getState>['outcomes'][number];
type MotionInitial = false | {
  opacity: number;
  y: number;
};
type MotionTransition = {
  duration: number;
} | typeof MOTION_ENTRANCE;
interface SelectOption {
  value: string;
  label: string;
}
const CATEGORY_OPTIONS: SelectOption[] = PROPOSAL_CATEGORIES.map((c): SelectOption => ({
  value: c.name,
  label: `${c.emoji} ${c.name}`
}));
const LOCATION_OPTIONS: SelectOption[] = PROPOSAL_LOCATIONS.map((l): SelectOption => ({
  value: l.name,
  label: `${l.emoji} ${l.name}`
}));
interface ProposalDetailsFormProps {
  locationEnabled: boolean;
  lockedCategory?: string | undefined;
}
export function ProposalDetailsForm(t0) {
  const $ = _c(82);
  const {
    locationEnabled,
    lockedCategory
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  const tErrors = useTranslations("launchpad.errors");
  const reduce = useReducedMotion();
  const aiStreamStatus = useProposalFormStore(_temp);
  const isStreaming = aiStreamStatus === "streaming";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectStepAttempted(DETAILS_STEP);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const attempted = useProposalFormStore(t1);
  const fullState = useProposalFormStore();
  const stateWithLockedCategory = lockedCategory === undefined ? fullState : {
    ...fullState,
    category: lockedCategory
  };
  const errors = getDetailsStepErrors(stateWithLockedCategory, {
    requireLocation: locationEnabled
  });
  const errText = function errText(field) {
    if (!attempted) {
      return;
    }
    const entry = errors[field];
    if (entry === undefined) {
      return;
    }
    return tErrors(entry.key as Parameters<typeof tErrors>[0], entry.params);
  };
  const title = useProposalFormStore(_temp2);
  const description = useProposalFormStore(_temp3);
  const storedCategory = useProposalFormStore(_temp4);
  const category = lockedCategory ?? storedCategory;
  const location = useProposalFormStore(_temp5);
  const marketTicker = useProposalFormStore(_temp6);
  const coverImageUrl = useProposalFormStore(_temp7);
  const coverImagePreview = useProposalFormStore(_temp8);
  const coverImageDraft = useProposalFormStore(_temp9);
  const coverImageUploading = useProposalFormStore(_temp10);
  const outcomes = useProposalFormStore(_temp11);
  const coverPinFailed = coverImagePreview !== undefined && coverImagePreview.length > 0 && coverImageDraft === null && coverImageUrl.length === 0 && !coverImageUploading;
  const updateField = useProposalFormStore(_temp12);
  const updateOutcomeLabel = useProposalFormStore(_temp13);
  const updateOutcomeTicker = useProposalFormStore(_temp14);
  const addOutcome = useProposalFormStore(_temp15);
  const removeOutcome = useProposalFormStore(_temp16);
  const setOutcomeImageDraft = useProposalFormStore(_temp17);
  const setOutcomeImage = useProposalFormStore(_temp18);
  const setCoverImageDraft = useProposalFormStore(_temp19);
  const setCoverImage = useProposalFormStore(_temp20);
  let t2;
  if ($[1] !== reduce) {
    t2 = reduce === true ? {
      duration: 0
    } : MOTION_ENTRANCE;
    $[1] = reduce;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const motionTransition = t2;
  const motionInitial = reduce === true ? false : FADE_INITIAL;
  const pending = function pending(value) {
    return isStreaming && value.length === 0;
  };
  let t3;
  if ($[3] !== outcomes) {
    t3 = outcomes.some(_temp21);
    $[3] = outcomes;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const outcomesArrived = t3;
  const outcomesPending = isStreaming && !outcomesArrived;
  let t4;
  if ($[5] !== isStreaming || $[6] !== title) {
    t4 = !isStreaming && detectPredictionStyle(title);
    $[5] = isStreaming;
    $[6] = title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const showPredictionStyleWarning = t4;
  const {
    minOutcomes,
    maxOutcomes
  } = useOutcomesLimits();
  const limitsReady = minOutcomes !== null && maxOutcomes !== null;
  const canAddMore = limitsReady && outcomes.length < maxOutcomes;
  const canRemove = limitsReady && outcomes.length > minOutcomes;
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const coverNsfw = useNsfwStatusByUrl(coverImageDraft === null ? coverImageUrl : null);
  let t5;
  if ($[8] !== setCoverPickerOpen) {
    t5 = function handleOpenCoverPicker() {
      setCoverPickerOpen(true);
    };
    $[8] = setCoverPickerOpen;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleOpenCoverPicker = t5;
  let t6;
  if ($[10] !== setCoverImageDraft) {
    t6 = function handleCoverImagePending(draft) {
      setCoverImageDraft(draft);
    };
    $[10] = setCoverImageDraft;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleCoverImagePending = t6;
  let t7;
  if ($[12] !== setCoverImage) {
    t7 = function handleCoverImageReject(_error) {
      setCoverImage("", false);
    };
    $[12] = setCoverImage;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleCoverImageReject = t7;
  let t8;
  if ($[14] !== setCoverImage) {
    t8 = function handleCoverImageRemove(e) {
      e.stopPropagation();
      setCoverImage("", false);
    };
    $[14] = setCoverImage;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleCoverImageRemove = t8;
  let t9;
  if ($[16] !== setOutcomeImageDraft) {
    t9 = function handleOutcomeImagePending(index, draft_0) {
      setOutcomeImageDraft(index, draft_0);
    };
    $[16] = setOutcomeImageDraft;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleOutcomeImagePending = t9;
  let t10;
  if ($[18] !== setOutcomeImage) {
    t10 = function handleOutcomeImageReject(index_0, _error_0) {
      setOutcomeImage(index_0, "", false);
    };
    $[18] = setOutcomeImage;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleOutcomeImageReject = t10;
  let t11;
  if ($[20] !== setOutcomeImage) {
    t11 = function handleOutcomeImageRemove(index_1) {
      setOutcomeImage(index_1, "", false);
    };
    $[20] = setOutcomeImage;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleOutcomeImageRemove = t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <AiStreamStatusChip />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== showPredictionStyleWarning || $[24] !== tForm) {
    t13 = showPredictionStyleWarning && <output className="flex gap-2 rounded-control border border-feedback-warning-border/30 bg-feedback-warning-accent/10 px-3 py-2 text-body text-feedback-warning-content"><Icon icon={Alert01IconSolid} className="mt-0.5 size-4 shrink-0" /><div className="flex flex-col gap-0.5"><p className="font-medium">{tForm("predictionStyleWarningTitle")}</p><p className="text-feedback-warning-content/80">{tForm("predictionStyleWarningBody", {
            exampleA: tForm("predictionStyleWarningExampleA"),
            exampleB: tForm("predictionStyleWarningExampleB")
          })}</p></div></output>;
    $[23] = showPredictionStyleWarning;
    $[24] = tForm;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== coverImagePreview || $[27] !== coverImageUrl || $[28] !== coverNsfw.isNsfw || $[29] !== coverPinFailed || $[30] !== handleCoverImageRemove || $[31] !== handleOpenCoverPicker || $[32] !== isStreaming || $[33] !== motionInitial || $[34] !== motionTransition) {
    t14 = <CoverImageSlot coverImageUrl={coverImageUrl} coverImagePreview={coverImagePreview} pinFailed={coverPinFailed} isStreaming={isStreaming} isFlagged={coverNsfw.isNsfw} onClickOpen={handleOpenCoverPicker} onRemove={handleCoverImageRemove} motionInitial={motionInitial} motionTransition={motionTransition} />;
    $[26] = coverImagePreview;
    $[27] = coverImageUrl;
    $[28] = coverNsfw.isNsfw;
    $[29] = coverPinFailed;
    $[30] = handleCoverImageRemove;
    $[31] = handleOpenCoverPicker;
    $[32] = isStreaming;
    $[33] = motionInitial;
    $[34] = motionTransition;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== tForm) {
    t15 = tForm("marketTickerLabel");
    $[36] = tForm;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== updateField) {
    t16 = e_1 => {
      const next = e_1.target.value;
      updateField("marketTicker", next);
    };
    $[38] = updateField;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== tForm) {
    t17 = tForm("marketTickerPlaceholder");
    $[40] = tForm;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const T0 = CategoryLocationFields;
  const t18 = lockedCategory !== undefined;
  const t19 = errText("category");
  const t20 = errText("location");
  let t21;
  if ($[42] !== T0 || $[43] !== category || $[44] !== isStreaming || $[45] !== location || $[46] !== locationEnabled || $[47] !== motionInitial || $[48] !== motionTransition || $[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== updateField) {
    t21 = <T0 category={category} categoryLocked={t18} location={location} locationEnabled={locationEnabled} categoryError={t19} locationError={t20} isStreaming={isStreaming} updateField={updateField} motionInitial={motionInitial} motionTransition={motionTransition} />;
    $[42] = T0;
    $[43] = category;
    $[44] = isStreaming;
    $[45] = location;
    $[46] = locationEnabled;
    $[47] = motionInitial;
    $[48] = motionTransition;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = updateField;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== addOutcome || $[55] !== canAddMore || $[56] !== canRemove || $[57] !== description || $[58] !== errText || $[59] !== handleOutcomeImagePending || $[60] !== handleOutcomeImageReject || $[61] !== handleOutcomeImageRemove || $[62] !== isStreaming || $[63] !== marketTicker || $[64] !== motionInitial || $[65] !== motionTransition || $[66] !== outcomes || $[67] !== outcomesPending || $[68] !== removeOutcome || $[69] !== title || $[70] !== updateOutcomeLabel || $[71] !== updateOutcomeTicker) {
    t22 = <OutcomesSection outcomesPending={outcomesPending} outcomes={outcomes} errText={errText} marketTicker={marketTicker} title={title} description={description} isStreaming={isStreaming} canRemove={canRemove} canAddMore={canAddMore} updateOutcomeLabel={updateOutcomeLabel} updateOutcomeTicker={updateOutcomeTicker} handleOutcomeImagePending={handleOutcomeImagePending} handleOutcomeImageReject={handleOutcomeImageReject} handleOutcomeImageRemove={handleOutcomeImageRemove} removeOutcome={removeOutcome} addOutcome={addOutcome} motionInitial={motionInitial} motionTransition={motionTransition} />;
    $[54] = addOutcome;
    $[55] = canAddMore;
    $[56] = canRemove;
    $[57] = description;
    $[58] = errText;
    $[59] = handleOutcomeImagePending;
    $[60] = handleOutcomeImageReject;
    $[61] = handleOutcomeImageRemove;
    $[62] = isStreaming;
    $[63] = marketTicker;
    $[64] = motionInitial;
    $[65] = motionTransition;
    $[66] = outcomes;
    $[67] = outcomesPending;
    $[68] = removeOutcome;
    $[69] = title;
    $[70] = updateOutcomeLabel;
    $[71] = updateOutcomeTicker;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== handleCoverImagePending || $[74] !== setCoverPickerOpen) {
    t23 = draft_1 => {
      setCoverPickerOpen(false);
      handleCoverImagePending(draft_1);
    };
    $[73] = handleCoverImagePending;
    $[74] = setCoverPickerOpen;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== coverPickerOpen || $[77] !== handleCoverImageReject || $[78] !== setCoverPickerOpen || $[79] !== t23 || $[80] !== title) {
    t24 = <MarketImagePickerModal open={coverPickerOpen} onOpenChange={setCoverPickerOpen} mode="cover" marketTitle={title} onPending={t23} onReject={handleCoverImageReject} />;
    $[76] = coverPickerOpen;
    $[77] = handleCoverImageReject;
    $[78] = setCoverPickerOpen;
    $[79] = t23;
    $[80] = title;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  return <LazyMotion features={domAnimation}><div className="flex flex-col gap-4">{t12}{t13}<div className="flex items-stretch gap-3">{t14}<div className="flex min-w-0 flex-1 flex-col gap-3">{pending(title) ? <SkeletonField label={t("fields.title")} className="h-9" /> : <m.div key="title" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><InputField label={t("fields.title")} value={title} onChange={e_0 => updateField("title", e_0.target.value)} maxLength={PROPOSAL_FIELDS.title.maxLength} error={errText("title")} /></m.div>}<InputField id="ai-market-ticker" label={t15} value={marketTicker} onChange={t16} placeholder={t17} prefix="$" error={errText("marketTicker")} /></div></div>{pending(description) ? <SkeletonField label={t("fields.description")} className="h-20" /> : <m.div key="description" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><TextareaField label={t("fields.description")} value={description} onChange={e_2 => updateField("description", e_2.target.value)} rows={3} error={errText("description")} /></m.div>}{t21}{t22}{t24}</div></LazyMotion>;
}

// ---------------------------------------------------------------------------
// Cover image slot
// ---------------------------------------------------------------------------
function _temp21(o) {
  return o.label.trim().length > 0;
}
function _temp20(s_18) {
  return s_18.setCoverImage;
}
function _temp19(s_17) {
  return s_17.setCoverImageDraft;
}
function _temp18(s_16) {
  return s_16.setOutcomeImage;
}
function _temp17(s_15) {
  return s_15.setOutcomeImageDraft;
}
function _temp16(s_14) {
  return s_14.removeOutcome;
}
function _temp15(s_13) {
  return s_13.addOutcome;
}
function _temp14(s_12) {
  return s_12.updateOutcomeTicker;
}
function _temp13(s_11) {
  return s_11.updateOutcomeLabel;
}
function _temp12(s_10) {
  return s_10.updateField;
}
function _temp11(s_9) {
  return s_9.outcomes;
}
function _temp10(s_8) {
  return s_8.coverImageUploading;
}
function _temp9(s_7) {
  return s_7.coverImageDraft;
}
function _temp8(s_6) {
  return s_6.coverImagePreview;
}
function _temp7(s_5) {
  return s_5.coverImageUrl;
}
function _temp6(s_4) {
  return s_4.marketTicker;
}
function _temp5(s_3) {
  return s_3.location;
}
function _temp4(s_2) {
  return s_2.category;
}
function _temp3(s_1) {
  return s_1.description;
}
function _temp2(s_0) {
  return s_0.title;
}
function _temp(s) {
  return s.aiStreamStatus;
}
interface CoverImageSlotProps {
  /** Durable IPFS URL (submission source of truth). Empty until `cover.pinned`. */
  coverImageUrl: string;
  /** Inline preview data-URI (display source of truth during AI review). */
  coverImagePreview?: string | undefined;
  /** True once the pin permanently failed — preview still shows, but the slot
   *  has no durable URL so submission is blocked; we flag it for the user. */
  pinFailed: boolean;
  isStreaming: boolean;
  isFlagged: boolean;
  onClickOpen: () => void;
  onRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  motionInitial: false | {
    opacity: number;
    y: number;
  };
  motionTransition: {
    duration: number;
  } | typeof MOTION_ENTRANCE;
}

/**
 * Cover-image specialisation of the shared `StreamedImageSlot`. Three
 * states (populated / streaming / empty) are owned by the lifted
 * component; this wrapper just threads the localised alt + remove
 * labels through (#34 audit).
 */
function CoverImageSlot(t0) {
  const $ = _c(21);
  const {
    coverImageUrl,
    coverImagePreview,
    pinFailed,
    isStreaming,
    isFlagged,
    onClickOpen,
    onRemove,
    motionInitial,
    motionTransition
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("fields.coverImage");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tForm) {
    t2 = tForm("coverImageRemove");
    $[2] = tForm;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== tForm) {
    t3 = tForm("coverImageFailedTitle");
    $[4] = tForm;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== coverImagePreview) {
    t4 = coverImagePreview !== undefined ? {
      imagePreview: coverImagePreview
    } : {};
    $[6] = coverImagePreview;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== coverImageUrl || $[9] !== isFlagged || $[10] !== isStreaming || $[11] !== motionInitial || $[12] !== motionTransition || $[13] !== onClickOpen || $[14] !== onRemove || $[15] !== pinFailed || $[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
    t5 = <StreamedImageSlot imageUrl={coverImageUrl} isStreaming={isStreaming} isFlagged={isFlagged} onClickOpen={onClickOpen} onRemove={onRemove} motionInitial={motionInitial} motionTransition={motionTransition} altLabel={t1} removeLabel={t2} pinFailed={pinFailed} pinFailedLabel={t3} {...t4} />;
    $[8] = coverImageUrl;
    $[9] = isFlagged;
    $[10] = isStreaming;
    $[11] = motionInitial;
    $[12] = motionTransition;
    $[13] = onClickOpen;
    $[14] = onRemove;
    $[15] = pinFailed;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}

// ---------------------------------------------------------------------------
// Category + Location fields
// ---------------------------------------------------------------------------

type UpdateField = ReturnType<typeof useProposalFormStore.getState>['updateField'];
interface CategoryLocationFieldsProps {
  category: string;
  categoryLocked: boolean;
  location: string;
  locationEnabled: boolean;
  categoryError?: string | undefined;
  locationError?: string | undefined;
  isStreaming: boolean;
  updateField: UpdateField;
  motionInitial: MotionInitial;
  motionTransition: MotionTransition;
}

/**
 * Category + Location 2-col grid, mirroring the manual flow's step1. While
 * streaming AND the slice is still empty we show a `<SkeletonField>`; the
 * moment a value lands the `<SelectField>` renders. State stays in
 * `ProposalDetailsForm` — this is a pure render of the threaded values/setter.
 */
function CategoryLocationFields(t0) {
  const $ = _c(28);
  const {
    category,
    categoryLocked,
    location,
    locationEnabled,
    categoryError,
    locationError,
    isStreaming,
    updateField,
    motionInitial,
    motionTransition
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  let t1;
  if ($[0] !== isStreaming) {
    t1 = function pending(value) {
      return isStreaming && value.length === 0;
    };
    $[0] = isStreaming;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pending = t1;
  if (categoryLocked && !locationEnabled) {
    return null;
  }
  const t2 = locationEnabled && !categoryLocked && "sm:grid-cols-2";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("grid grid-cols-1 gap-3", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== category || $[5] !== categoryError || $[6] !== categoryLocked || $[7] !== motionInitial || $[8] !== motionTransition || $[9] !== pending || $[10] !== t || $[11] !== tForm || $[12] !== updateField) {
    t4 = !categoryLocked && (pending(category) ? <SkeletonField label={t("fields.category")} className="h-9" /> : <m.div key="category" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><SelectField label={tForm("categoryLabel")} items={CATEGORY_OPTIONS} value={category.length > 0 ? category : undefined} onValueChange={v => updateField("category", v)} placeholder={tForm("categoryPlaceholder")} error={categoryError} /></m.div>);
    $[4] = category;
    $[5] = categoryError;
    $[6] = categoryLocked;
    $[7] = motionInitial;
    $[8] = motionTransition;
    $[9] = pending;
    $[10] = t;
    $[11] = tForm;
    $[12] = updateField;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== location || $[15] !== locationEnabled || $[16] !== locationError || $[17] !== motionInitial || $[18] !== motionTransition || $[19] !== pending || $[20] !== t || $[21] !== tForm || $[22] !== updateField) {
    t5 = locationEnabled ? pending(location) ? <SkeletonField label={t("fields.location")} className="h-9" /> : <m.div key="location" initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><SelectField label={tForm("locationLabel")} items={LOCATION_OPTIONS} value={location.length > 0 ? location : undefined} onValueChange={v_0 => updateField("location", v_0)} placeholder={tForm("locationPlaceholder")} error={locationError} /></m.div> : null;
    $[14] = location;
    $[15] = locationEnabled;
    $[16] = locationError;
    $[17] = motionInitial;
    $[18] = motionTransition;
    $[19] = pending;
    $[20] = t;
    $[21] = tForm;
    $[22] = updateField;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t3 || $[25] !== t4 || $[26] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Outcomes section
// ---------------------------------------------------------------------------

interface OutcomesSectionProps {
  outcomesPending: boolean;
  outcomes: AiOutcome[];
  /** Translate a step-2 validation error for a given field key, gated on
   *  `stepsAttempted`. Returns undefined when valid / not yet attempted. */
  errText: (field: string) => string | undefined;
  marketTicker: string;
  title: string;
  description: string;
  isStreaming: boolean;
  canRemove: boolean;
  canAddMore: boolean;
  updateOutcomeLabel: (index: number, label: string) => void;
  updateOutcomeTicker: (index: number, ticker: string) => void;
  handleOutcomeImagePending: (index: number, draft: MarketImageDraft) => void;
  handleOutcomeImageReject: (index: number, error: Error) => void;
  handleOutcomeImageRemove: (index: number) => void;
  removeOutcome: (index: number) => void;
  addOutcome: () => void;
  motionInitial: MotionInitial;
  motionTransition: MotionTransition;
}

/**
 * Outcomes block — reuses the manual flow's `<OutcomeInput>` so the row
 * shape, ticker prefix, image picker, and remove affordance are
 * byte-identical between flows. All state stays owned by `ProposalDetailsForm`;
 * this component is a pure render of the values/setters threaded in.
 */
function OutcomesSection(t0) {
  const $ = _c(36);
  const {
    outcomesPending,
    outcomes,
    errText,
    marketTicker,
    title,
    description,
    isStreaming,
    canRemove,
    canAddMore,
    updateOutcomeLabel,
    updateOutcomeTicker,
    handleOutcomeImagePending,
    handleOutcomeImageReject,
    handleOutcomeImageRemove,
    removeOutcome,
    addOutcome,
    motionInitial,
    motionTransition
  } = t0;
  const t = useTranslations("aiProposal.editForm");
  const tForm = useTranslations("launchpad.form");
  const showSimilarMarkets = useProposalFormStore(_temp22);
  let t1;
  if ($[0] !== t) {
    t1 = t("fields.outcomes");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex items-center justify-between"><Label>{t1}</Label></div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canRemove || $[5] !== errText || $[6] !== handleOutcomeImagePending || $[7] !== handleOutcomeImageReject || $[8] !== handleOutcomeImageRemove || $[9] !== isStreaming || $[10] !== marketTicker || $[11] !== motionInitial || $[12] !== motionTransition || $[13] !== outcomes || $[14] !== outcomesPending || $[15] !== removeOutcome || $[16] !== title || $[17] !== updateOutcomeLabel || $[18] !== updateOutcomeTicker) {
    t3 = outcomesPending ? <div className="flex flex-col gap-2"><Skeleton className="h-24 w-full rounded-control" /><Skeleton className="h-24 w-full rounded-control" /></div> : <div className="flex flex-col gap-3">{outcomes.map((outcome, index) => <m.div key={outcome.id} initial={motionInitial} animate={FADE_ANIMATE} transition={motionTransition}><OutcomeInput index={index} label={outcome.label} ticker={outcome.ticker} error={errText(`outcomeLabel_${String(index)}`) ?? errText(`outcomeDuplicate_${String(index)}`) ?? null} tickerError={errText(`outcomeTicker_${String(index)}`) ?? errText(`outcomeTickerDuplicate_${String(index)}`) ?? null} marketTicker={marketTicker} marketTitle={title} imageUrl={outcome.imageUrl !== "" ? outcome.imageUrl : null} onLabelChange={label => updateOutcomeLabel(index, label)} onTickerChange={ticker => updateOutcomeTicker(index, ticker)} onImagePending={draft => handleOutcomeImagePending(index, draft)} onImageReject={err => handleOutcomeImageReject(index, err)} onImageRemove={() => handleOutcomeImageRemove(index)} onRemove={() => removeOutcome(index)} canRemove={canRemove} imageLoading={isStreaming && outcome.imageUrl === "" && (outcome.imagePreview === undefined || outcome.imagePreview.length === 0)} eagerImage={true} pinFailed={outcome.imagePreview !== undefined && outcome.imagePreview.length > 0 && outcome.imageDraft === null && outcome.imageUrl === "" && !outcome.imageUploading} {...outcome.imagePreview !== undefined ? {
          imagePreview: outcome.imagePreview
        } : {}} prefetchImageSearch={false} /></m.div>)}</div>;
    $[4] = canRemove;
    $[5] = errText;
    $[6] = handleOutcomeImagePending;
    $[7] = handleOutcomeImageReject;
    $[8] = handleOutcomeImageRemove;
    $[9] = isStreaming;
    $[10] = marketTicker;
    $[11] = motionInitial;
    $[12] = motionTransition;
    $[13] = outcomes;
    $[14] = outcomesPending;
    $[15] = removeOutcome;
    $[16] = title;
    $[17] = updateOutcomeLabel;
    $[18] = updateOutcomeTicker;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = !canAddMore || outcomesPending;
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={PlusSignIcon} className="size-4" />;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== tForm) {
    t6 = tForm("addOutcome");
    $[21] = tForm;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== addOutcome || $[24] !== t4 || $[25] !== t6) {
    t7 = <AnimatedButton type="button" variant="secondary" onClick={addOutcome} disabled={t4} className="w-full">{t5}{t6}</AnimatedButton>;
    $[23] = addOutcome;
    $[24] = t4;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== description || $[28] !== showSimilarMarkets || $[29] !== title) {
    t8 = showSimilarMarkets ? <SimilarMarketsNudge queryText={`${title}\n${description}`} /> : null;
    $[27] = description;
    $[28] = showSimilarMarkets;
    $[29] = title;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t2 || $[32] !== t3 || $[33] !== t7 || $[34] !== t8) {
    t9 = <div className="flex flex-col gap-3">{t2}{t3}{t7}{t8}</div>;
    $[31] = t2;
    $[32] = t3;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
}

// ---------------------------------------------------------------------------
// Skeleton field
// ---------------------------------------------------------------------------

// SkeletonField lifted to `@/components/launchpad/ai/skeleton-field`
// so the Suggested-outcomes wizard shares the same streamed-field
// skeleton (#34 audit).
function _temp22(state) {
  return state.aiMarketKind !== "prediction";
}